import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { checkIfComponentIsFormComponent, mergeListViewComponentCurrentValue, uploadFiles } from '@/views/apps/_customPage/functions'
import { type ApiCustomPageComponents, type CPInputConfiguration, type ExtendedBlock, type InputCheckbox, type InputCheckboxList, type InputConsentList, type InputCountry, type InputDate, type InputDateTime, type InputDocument, type InputFile, type InputDropdown, type InputNumber, type InputPhoneNumber, type InputRadio, type InputRadioList, type ListView, type Toggle } from 'widget-components'
import type { FixMe } from '@/types/globals'
import type { BlockPayload } from '../types'

dayjs.extend(customParseFormat)

export const BLOCK_TYPES = {
    GRAPHIC: 'graphic',
    TEXT: 'text',
    FORM: 'form',
    ACTION: 'action',
    OTHER: 'other',
}

export const CUSTOM_BLOCK_MAP: Record<string, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transform: (block: any) => Promise<BlockPayload> | BlockPayload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentValueEmpty?: (block: any) => boolean
}> = {
    INPUT_RADIO: {
        transform: (block: ExtendedBlock<InputRadio>) => {
            const selectedItemId = block.items.find((item) => item.value === block.currentValue)?.itemId

            return {
                id: block.id,
                type: block.type,
                selectedItemId,
            }
        },
    },
    INPUT_CHECKBOX: {
        transform: (block: ExtendedBlock<InputCheckbox>) => ({
            id: block.id,
            type: block.type,
            selected: block.currentValue,
        }),
    },
    INPUT_DATE: {
        transform: (block: ExtendedBlock<InputDate>) => {
            const date = dayjs(block.currentValue as string, block.format)

            return {
                id: block.id,
                type: block.type,
                value: date.isValid() ? date.format('YYYY-MM-DD') : '',
            }
        },
    },
    INPUT_DATE_TIME: {
        transform: (block: ExtendedBlock<InputDateTime>) => {
            const date = dayjs(block.currentValue as string, block.format)

            return {
                id: block.id,
                type: block.type,
                value: date.isValid() ? date.toISOString() : '',
            }
        },
    },
    INPUT_CONSENT_LIST: {
        transform: (block: ExtendedBlock<InputConsentList>) => {
            const selectedItemIds: Array<string> = [];

            (block.currentValue as Array<Record<string, string>>)?.forEach((item: Record<string, string>) => {
                if (item.selected) {
                    selectedItemIds.push(item.itemId)
                }
            })

            return {
                id: block.id,
                type: block.type,
                selectedItemIds,
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputConsentList>) => {
            return block.currentValue.every((item: Record<string, string>) => !item.selected)
        },
    },
    INPUT_DROPDOWN: {
        transform: (block: ExtendedBlock<InputDropdown>) => {
            const selectedItemIds = []

            // multi-select dropdown
            if (Array.isArray(block.currentValue)) {
                block.currentValue.forEach((item: Record<string, string>) => {
                    selectedItemIds.push(item.itemId)
                })
            // single select dropdown
            } else if (block.currentValue instanceof Object) {
                selectedItemIds.push(block.currentValue.itemId)
            }

            return {
                id: block.id,
                type: block.type,
                selectedItemIds: selectedItemIds.filter(v => v),
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputCountry>) => {
            return Array.isArray(block.currentValue)
                ? block.currentValue.length === 0
                : !block.currentValue || !block.currentValue.itemId
        },
    },
    INPUT_COUNTRY: {
        transform: (block: ExtendedBlock<InputCountry>) => {
            const selectedItemIds = []

            // multi-select dropdown
            if (Array.isArray(block.currentValue)) {
                block.currentValue.forEach((item: Record<string, string>) => {
                    selectedItemIds.push(item.itemId)
                })
            // single select dropdown
            } else if (block.currentValue instanceof Object) {
                selectedItemIds.push(block.currentValue.itemId)
            }

            return {
                id: block.id,
                type: block.type,
                selectedItemIds: selectedItemIds.filter(v => v),
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputCountry>) => {
            return Array.isArray(block.currentValue)
                ? block.currentValue.length === 0
                : !block.currentValue || !block.currentValue.itemId
        },
    },
    // DEPRECATED
    INPUT_DOCUMENT: {
        transform: async (block: ExtendedBlock<InputDocument>) => {
            const files = (block.currentValue as Record<string, FixMe>)?.document?.files || {}

            const error = Object
                .values(files)
                .map((f) => (f as Record<'error', string>).error)
                .filter(Boolean)
                .join(', ')

            if (error) {
                return {
                    id: block.id,
                    type: block.type,
                    value: [],
                }
            }

            return {
                id: block.id,
                type: block.type,
                value: await uploadFiles(block),
            }
        },
    },
    INPUT_FILE: {
        transform: async (block: ExtendedBlock<InputFile>) => {
            const files = (block.currentValue as Record<string, FixMe>)?.document?.files || {}

            const error = Object
                .values(files)
                .map((f) => (f as Record<'error', string>).error)
                .filter(Boolean)
                .join(', ')

            if (error) {
                return {
                    id: block.id,
                    type: block.type,
                    value: [],
                }
            }

            return {
                id: block.id,
                type: block.type,
                value: await uploadFiles(block),
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputFile>) => {
            const files = (block.currentValue as Record<string, FixMe>)?.document?.files || {}
            return Object.keys(files).length === 0
        },
    },
    INPUT_CHECKBOX_LIST: {
        transform: (block: ExtendedBlock<InputCheckboxList>) => {
            return {
                id: block.id,
                type: block.type,
                index: block.currentValue,
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputCheckboxList>) => block.currentValue === -1 || block.currentValue === null,
    },
    INPUT_RADIO_LIST: {
        transform: (block: ExtendedBlock<InputRadioList>) => {
            return {
                id: block.id,
                type: block.type,
                index: block.currentValue,
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputRadioList>) => block.currentValue === -1 || block.currentValue === null,
    },
    LIST_VIEW: {
        transform: (block: ExtendedBlock<ListView>) => ({
            id: block.id,
            type: block.type,
            items: [],
        }),
    },
    TOGGLE: {
        transform: (block: ExtendedBlock<Toggle>) => ({
            id: block.id,
            type: block.type,
            selected: block.currentValue,
        }),
    },
    INPUT_PHONE_NUMBER: {
        transform: (block: ExtendedBlock<InputPhoneNumber>) => {
            const countryCode = block.currentValue?.countryCode
            const number = block.currentValue?.number.replace(/\s+/g, '')

            return {
                id: block.id,
                type: block.type,
                value: {
                    countryCode,
                    number,
                },
            }
        },
        currentValueEmpty: (block: ExtendedBlock<InputPhoneNumber>) => {
            if (!block.currentValue) return true
            const countryCode = block.currentValue?.countryCode
            const number = block.currentValue?.number
            return !countryCode || !number
        },
    },
}

export const BLOCK_ACTIONS_MAP = {
    INPUT_RADIO: {
        actionName: 'radioValueSubmitted',
        transform(block: ExtendedBlock<InputRadio>) {
            return [{
                actionName: this.actionName,
                props: {
                    fieldName: block.fieldName,
                    fieldValue: block.currentValue,
                },
            }]
        },
    },
    INPUT_CHECKBOX: {
        actionName: 'checkboxValueSubmitted',
        transform(block: ExtendedBlock<InputCheckbox>) {
            return [{
                actionName: this.actionName,
                props: {
                    fieldName: block.fieldName,
                    fieldValue: block.label,
                    state: block.currentValue,
                },
            }]
        },
    },
    INPUT_SLIDER: {
        actionName: 'sliderValueSubmitted',
    },
    INPUT_DROPDOWN: {
        actionName: 'dropdownValueSubmitted',
        transform(block: ExtendedBlock<InputDropdown>) {
            const fieldValues = []

            // multi-select dropdown
            if (Array.isArray(block.currentValue)) {
                block.currentValue.forEach((item: FixMe) => {
                    fieldValues.push(item.value)
                })
                // single select dropdown
            } else if (block.currentValue instanceof Object) {
                fieldValues.push(block.currentValue.value)
            }

            return [{
                actionName: this.actionName,
                props: {
                    fieldName: block.fieldName,
                    fieldValues,
                },
            }]
        },
    },
    INPUT_COUNTRY: {
        actionName: 'countryValueSubmitted',
        transform(block: ExtendedBlock<InputCountry>) {
            const fieldValues = []

            // multi-select dropdown
            if (Array.isArray(block.currentValue)) {
                block.currentValue.forEach((item: FixMe) => {
                    fieldValues.push(item.value)
                })
                // single select dropdown
            } else if (block.currentValue instanceof Object) {
                fieldValues.push(block.currentValue.value)
            }

            return [{
                actionName: this.actionName,
                props: {
                    fieldName: block.fieldName,
                    fieldValues,
                },
            }]
        },
    },
}

export const COMPONENT_CONDITIONS_MODEL_VALUE_MAP: Record<string, FixMe> = {
    INPUT_DROPDOWN: (block: ExtendedBlock<InputDropdown>) => {
        // multi-select dropdown
        if (Array.isArray(block.currentValue)) {
            return block.currentValue.map((item: Record<string, string>) => {
                return item.value
            })
        }

        // single select dropdown
        return block.currentValue?.value
    },
    INPUT_COUNTRY: (block: ExtendedBlock<InputCountry>) => {
        // multi-select dropdown
        if (Array.isArray(block.currentValue)) {
            return block.currentValue.map((item: Record<string, string>) => {
                return item.countryCode
            })
        }

        // single select dropdown
        return block.currentValue?.countryCode
    },
    INPUT_CONSENT_LIST: (block: ExtendedBlock<InputConsentList>) => {
        return (block.currentValue as Array<Record<string, string>>)?.map((item: Record<string, string>) => {
            return item.selected
        })
    },
    INPUT_CHECKBOX_LIST: (block: ExtendedBlock<InputCheckboxList>) => {
        return typeof block.currentValue === 'number' ? block.currentValue : -1
    },
    INPUT_RADIO_LIST: (block: ExtendedBlock<InputRadioList>) => {
        return typeof block.currentValue === 'number' ? block.currentValue : -1
    },
    LIST_VIEW: (block: ExtendedBlock<ListView>) => {
        return block.items.reduce((result, innerBlock, innerBlockIndex) => {
            const innerBlocksValues = mergeListViewComponentCurrentValue(
                block,
                innerBlockIndex,
                innerBlock.components as ExtendedBlock<ApiCustomPageComponents>[],
            )
                .filter(b => checkIfComponentIsFormComponent(b as ExtendedBlock))
                .map(b => {
                    const innerBlockComponent = (b as ExtendedBlock<CPInputConfiguration>)

                    return [
                        innerBlockComponent.fieldName, // KEY
                        getBlockValueForConditions(innerBlockComponent), // VALUE
                    ]
                })

            result.push(Object.fromEntries(innerBlocksValues))

            return result
        }, [] as Record<string, unknown>[]) || []
    },
    INPUT_NUMBER: (block: ExtendedBlock<InputNumber>) => {
        return typeof block.currentValue !== 'number' ? undefined : (block.currentValue ?? block.defaultValue)
    },
}

export const getBlockValueForConditions = (block: ExtendedBlock<CPInputConfiguration>) => {
    const mapValue = COMPONENT_CONDITIONS_MODEL_VALUE_MAP[block.type as string]

    return mapValue ? mapValue(block) : block.currentValue ?? block.defaultValue
}
